<template>
  <CustomDialog
    v-model="showModal"
    title="enter_custom_price"
    :width="'600px'"
    :footer="true"
    :close-btn="true"
    @submit="addToCart"
    @input="handleModalClose"
  >
    <template #activator>
      <div class="dynamic-price-content" v-if="product">
        <v-container fluid>
          <!-- Product Information Card -->
          <v-card outlined class="mb-6 rounded-lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon small color="primary" left>fal fa-box</v-icon>
              {{ $t("product_information") }}
            </v-card-title>
            <v-divider class="mt-0" />

            <v-card-text class="pa-4">
              <v-row>
                <!-- Product Image -->
                <v-col cols="12" sm="4" class="text-center">
                  <v-avatar tile size="100" class="product-image-container">
                    <app-img
                      :img="product.img || '/images/no_photo.jpg'"
                      width="100"
                      height="100"
                    >
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-icon size="48" color="grey lighten-1"
                            >fal fa-image</v-icon
                          >
                        </v-row>
                      </template>
                    </app-img>
                  </v-avatar>
                </v-col>

                <!-- Product Details -->
                <v-col cols="12" sm="8">
                  <v-row dense>
                    <v-col cols="6" class="text-caption text--secondary">
                      {{ $t("product_name") }}
                    </v-col>
                    <v-col cols="6" class="text-right">
                      <span class="font-weight-medium">{{ product.name }}</span>
                    </v-col>

                    <v-col cols="6" class="text-caption text--secondary">
                      {{ $t("category") }}
                    </v-col>
                    <v-col cols="6" class="text-right">
                      <span
                        class="font-weight-medium"
                        v-if="product.category"
                        >{{ getCategoryName(product.category) }}</span
                      >
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Price Input Card -->
          <v-card outlined class="mb-6 rounded-lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon small color="success" left>fal fa-dollar-sign</v-icon>
              {{ $t("price") }}
            </v-card-title>
            <v-divider class="mt-0" />

            <v-card-text class="pa-4">
              <CurrencyInput
                ref="priceInput"
                v-model="customPrice"
                :suffix="currencyCode"
                label="price"
                :min="0"
              />
              <v-alert type="info" dense outlined class="mt-3">
                {{
                  $t("dynamic_pricing_info") ||
                  "You can enter any price for this product. The default price is shown above for reference."
                }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-container>
      </div>
    </template>
  </CustomDialog>
</template>

<script>
import CustomDialog from "@/components/customDialog.vue";
import CurrencyInput from "@/components/CurrencyInput";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  name: "DynamicPriceModal",
  components: {
    CustomDialog,
    CurrencyInput,
  },
  mixins: [myMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      quantity: 1,
      customPrice: 0,
      loading: false,
      defineStore: mainStore(),
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    defaultPrice() {
      if (!this.product || !this.product.id) return 0;
      return this.product.price_sale || 0;
    },
    currencyCode() {
      if (!this.product || !this.product.currency) return "";
      return this.product.currency.code || "";
    },
    availableStock() {
      if (!this.product || !this.product.id) return 0;

      // If track_stock is disabled, allow unlimited stock
      if (this.product.track_stock === 0 || this.product.track_stock === "0") {
        return 999999;
      }

      return this.product.stock || 0;
    },
    totalPrice() {
      return (this.customPrice || 0) * (this.quantity || 0);
    },
    canAddToCart() {
      if (!this.product || !this.product.id) return false;
      if (!this.customPrice || this.customPrice <= 0) return false;
      if (this.quantity <= 0) return false;

      // If track_stock is disabled, only check if quantity > 0
      if (this.product.track_stock === 0 || this.product.track_stock === "0") {
        return this.quantity > 0;
      }

      return this.quantity > 0 && this.quantity <= this.availableStock;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetForm();
          this.triggerFocus();
        }
      },
    },
    defaultPrice: {
      immediate: true,
      handler(newVal) {
        // Set custom price to default price when modal opens
        if (newVal && newVal > 0 && this.customPrice === 0) {
          this.customPrice = newVal;
        }
      },
    },
  },
  methods: {
    handleModalClose(value) {
      this.showModal = value;
      if (!value) {
        this.closeModal();
      }
    },
    incrementQuantity() {
      if (
        this.quantity <
        (this.product.track_stock === 0 || this.product.track_stock === "0"
          ? 1000
          : this.availableStock)
      ) {
        this.quantity++;
      }
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    setQuantity(qty) {
      if (
        qty > 0 &&
        (this.product.track_stock === 0 ||
          this.product.track_stock === "0" ||
          qty <= this.availableStock)
      ) {
        this.quantity = qty;
      }
    },
    validateQuantity() {
      if (this.quantity < 1) {
        this.quantity = 1;
      } else if (
        this.quantity > this.availableStock &&
        this.product.track_stock !== 0 &&
        this.product.track_stock !== "0"
      ) {
        this.quantity = this.availableStock;
      }
    },
    resetForm() {
      this.quantity = 1;
      this.customPrice = this.defaultPrice || 0;
    },
    closeModal() {
      this.showModal = false;
    },
    async addToCart() {
      if (!this.canAddToCart) return;

      try {
        this.loading = true;
        // Prepare cart data with custom price
        const cartData = {
          that: this,
          payload: this.product,
          selectedVariationOptions: null,
          qty: this.quantity,
          customPrice: this.customPrice, // Pass custom price
        };
        // Add to cart using store
        this.defineStore.addToCart(cartData);

        // Close modal
        this.closeModal();
      } catch (error) {
        console.error("Error adding to cart:", error);
        toast(this, "error_adding_to_cart", "error");
      } finally {
        this.loading = false;
      }
    },
    triggerFocus() {
      this.$nextTick(() => {
        // Try focusing multiple times with increasing delays
        // This handles slow transitions or Vuetify's internal focus theft
        const attemptFocus = (delay) => {
          setTimeout(() => {
            if (this.$refs.priceInput) {
              this.$refs.priceInput.focus();
              // Also try to select the text for easier editing
              if (this.$refs.priceInput.select) {
                this.$refs.priceInput.select();
              }
            }
          }, delay);
        };

        attemptFocus(100);
        attemptFocus(400);
        attemptFocus(800);
      });
    },
  },
  mounted() {
    // Also trigger on mount just in case
    if (this.show) {
      this.triggerFocus();
    }
  },
};
</script>

<style scoped>
.dynamic-price-content {
  max-height: 85vh;
  overflow-y: auto;
}

.product-image-container {
  border: 1px solid #e0e0e0;
}

.quantity-controls-container {
  text-align: center;
}

.quantity-display {
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.quantity-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1976d2;
  line-height: 1;
}

.quantity-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

.quantity-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.quantity-btn {
  border: 1px solid #e0e0e0;
}

.quantity-slider {
  flex: 1;
  max-width: 200px;
}

.quick-quantity {
  text-align: center;
}

.gap-2 {
  gap: 8px;
}

/* Responsive Design */
@media (max-width: 600px) {
  .dynamic-price-content {
    max-height: 80vh;
  }

  .quantity-buttons {
    flex-direction: column;
    gap: 16px;
  }

  .quantity-slider {
    max-width: 100%;
  }
}
</style>
